import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

import { QuickAnnotationAccess } from '@/components/annotation';
import { useAuth } from '@/components/auth';
import { isAdminUser } from '@/utils/admin';
import { Shield, User, Settings, Music, Brain, Star, Clock } from 'lucide-react';
import logo from '../logo.svg';

export function HomePage() {
  const { user, lastSyncEvent, participantProfile, isLoadingParticipant } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isAdminUser(user);

  return (
    <>
      {/* Header */}
      <div className="text-center mb-8">
        <img
          src={logo}
          alt="LAION Logo"
          className="h-24 mx-auto mb-4 transition-all duration-300 hover:drop-shadow-[0_0_2em_#646cffaa]"
        />
        <h1 className="text-4xl font-bold mb-2">AI Music Research Portal</h1>
        <p className="text-lg text-muted-foreground">
          Help advance AI music understanding through collaborative annotation
        </p>
      </div>

      <div className="space-y-6">
        {/* Welcome & Status */}
        <Card className="bg-card/50 backdrop-blur-sm border-muted">
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-green-600" />
                <span className="font-semibold text-lg">
                  Welcome, {user?.username}!
                </span>
                {isAdmin && (
                  <span className="ml-2 px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded-full">
                    Admin
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Authenticated with WebAuthn • {lastSyncEvent && `🔄 ${lastSyncEvent}`}
              </p>
            </div>

            {/* Participant Status */}
            {!isLoadingParticipant && participantProfile && (
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-full text-sm">
                  <User className="h-3 w-3 text-blue-600 dark:text-blue-400" />
                  <span className="text-blue-800 dark:text-blue-200">
                    {participantProfile.hasProfile 
                      ? 'Research participant profile active' 
                      : 'Ready to create research profile'
                    }
                  </span>
                </div>
              </div>
            )}

            {/* New User Instructions */}
            {!isLoadingParticipant && participantProfile && !participantProfile.hasProfile && (
              <div className="p-4 bg-green-50 dark:bg-green-950/50 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <User className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-green-800 dark:text-green-200">
                    Getting Started
                  </span>
                </div>
                <div className="text-sm text-green-700 dark:text-green-300 space-y-2">
                  <ol className="space-y-1">
                    <li>1. Your account is set up and ready to go!</li>
                    <li>2. Complete your research profile below with basic demographic information</li>
                    <li>3. Submit your profile to begin the annotation process</li>
                    <li>4. Start annotating music samples - quality over quantity matters, so take your time to provide thoughtful assessments</li>
                  </ol>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Research Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-xl">
              <Brain className="h-5 w-5 text-primary" />
              About This Research
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              Help us understand how people perceive and distinguish between AI-generated and human-composed music. 
              Your participation contributes to cutting-edge research in artificial intelligence and music cognition.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-blue-50 dark:bg-blue-950/50 rounded-lg">
                <Music className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <h3 className="font-semibold mb-1 text-sm">Listen & Identify</h3>
                <p className="text-xs text-muted-foreground">
                  Determine if music is AI-generated or human-composed
                </p>
              </div>
              <div className="text-center p-4 bg-purple-50 dark:bg-purple-950/50 rounded-lg">
                <Star className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <h3 className="font-semibold mb-1 text-sm">Rate Quality</h3>
                <p className="text-xs text-muted-foreground">
                  Evaluate aesthetics, emotional impact, and overall quality
                </p>
              </div>
              <div className="text-center p-4 bg-green-50 dark:bg-green-950/50 rounded-lg">
                <Clock className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <h3 className="font-semibold mb-1 text-sm">Quick Sessions</h3>
                <p className="text-xs text-muted-foreground">
                  Complete at your own pace, typically 15-20 minutes each
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Access to Annotation Platform */}
        <QuickAnnotationAccess onStart={() => navigate('/annotation')} />

        {/* My Rewards */}
        {participantProfile?.hasProfile && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-600" />
                My Rewards
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Track your progress and claim your Tensorpool credits
              </p>
              <Button asChild variant="outline" className="gap-2" size="lg">
                <Link to="/my-rewards">
                  <Star className="h-4 w-4" />
                  View Reward Status
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Admin Access */}
        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Administration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button asChild variant="outline" className="gap-2" size="lg">
                <Link to="/admin">
                  <Settings className="h-4 w-4" />
                  Admin Dashboard
                </Link>
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </>
  );
}